// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { UseMutationResult } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useErrorContext } from '../../contexts/ErrorContext';
import { parseApiError, withFriendlyError } from '../../utils/parseApiError';

const useMutate = () => {
  const { enqueueSnackbar } = useSnackbar();
  const errorContext = useErrorContext();
  const mutate = async <TData, TError, TVariables, TContext>(
    mutation: UseMutationResult<TData, TError, TVariables, TContext>,
    payload: TVariables,
    successMsg: string,
    errorMsg: string,
  ) => {
    try {
      const res = await mutation.mutateAsync(payload);
      enqueueSnackbar(successMsg, { variant: 'success' });
      return res;
    } catch (error) {
      const friendly = parseApiError(error, errorMsg);
      const err = withFriendlyError(new Error(friendly.message, { cause: error }), friendly);
      errorContext.enqueueError(err);
      throw err;
    }
  };
  return mutate;
};

export default useMutate;
